import { Reducer } from 'redux'
import { CloudProvidersType } from '../../../admin/modules/NewCustomers/reducer'
import { MappingManagerTemplateStatusType } from '../../../oppsync/modules/coSellMappingManager/reducer'
import { MarketplaceMappingManagerActionTypes } from './action'

export interface MarketplaceTemplatesDetailsData {
  templateName: string
  templateId: string
  templateType: string
  status:
    | MappingManagerTemplateStatusType.DRAFT
    | MappingManagerTemplateStatusType.ACTIVE
  createdBy: string
  creationDate: string
  lastUpdatedAt: string
}

export interface MarketplaceFieldMappingPicklistData {
  referralValue: string
  hubspotValue: string
  hubspotLabel: string
}

export interface MarketplaceFieldMapping {
  referralField: string
  referralFieldType: string
  hubspotObject: string
  hubspotField: string
  hubspotFieldLabel: string
  hubspotFieldType: string
  isHidden: boolean
  isReadonly: boolean
  isMapped: boolean
  label: string
  required: boolean
  displayOrder: number
  defaultValue: string | string[]
  category: string
  picklist: MarketplaceHubspotFieldsOptions[] | null
  picklistMappings: MarketplaceFieldMappingPicklistData[] | null
}

export interface MarketplaceTemplatesDataFieldMappings {
  awsAccountNumber?: MarketplaceFieldMapping
  companyName?: MarketplaceFieldMapping
  recipient?: MarketplaceFieldMapping
  customEmailMessage?: MarketplaceFieldMapping
  state?: MarketplaceFieldMapping
  contractDuration?: MarketplaceFieldMapping
  entitlementOption?: MarketplaceFieldMapping
  renewal?: MarketplaceFieldMapping
  eula?: MarketplaceFieldMapping
  customEula?: MarketplaceFieldMapping
}

export interface MarketplaceTemplateDataResponse {
  id: string
  name: string
  templateType: string
  autoReferralCreation: boolean
  autoReferralDealTypeTrigger: string
  cloudProvider: CloudProvidersType
  isDraft: boolean
  isActive: boolean
  fieldMappings: MarketplaceTemplatesDataFieldMappings
  createdAt: string
  updatedAt: string
  createdBy: string
  updatedBy: string
}

export interface MarketplaceTemplateData {
  templateData: MarketplaceTemplateDataResponse
}

export interface MarketplaceHubspotFieldsOptions {
  value: string
  label: string
}

export interface MarketplaceHubspotFieldsData {
  internalName: string
  displayLabel: string
  fieldType: string
  objectType: string
  description: string
  options?: MarketplaceHubspotFieldsOptions[]
}

export interface MarketplaceHubspotFieldsDataResponse {
  fields: MarketplaceHubspotFieldsData[]
}

export interface MarketplaceSalesforceFieldsData {
  internalName: string
  displayLabel: string
  fieldType: string
  objectType: string
  description: string
}

export interface MarketplaceSalesforceFieldsDataResponse {
  fields: MarketplaceSalesforceFieldsData[]
}

export interface MarketplaceMappingManagerData {
  templateDetails: MarketplaceTemplatesDetailsData[]
  templateData: MarketplaceTemplateData
  hubspotFields: MarketplaceHubspotFieldsDataResponse
  salesforceFields: MarketplaceSalesforceFieldsDataResponse
}

const initialState: MarketplaceMappingManagerData = {
  templateDetails: [] as MarketplaceTemplatesDetailsData[],
  templateData: {} as MarketplaceTemplateData,
  hubspotFields: {} as MarketplaceHubspotFieldsDataResponse,
  salesforceFields: {} as MarketplaceSalesforceFieldsDataResponse,
}

type actionType =
  | {
      type: MarketplaceMappingManagerActionTypes.SET_TEMPLATE_DATA_INFO
      payload: MarketplaceTemplatesDetailsData[]
    }
  | {
      type: MarketplaceMappingManagerActionTypes.SET_TEMPLATE_DATA
      payload: MarketplaceTemplateDataResponse
    }
  | {
      type: MarketplaceMappingManagerActionTypes.SET_TEMPLATE_AS_DRAFT
      payload: boolean
    }
  | {
      type: MarketplaceMappingManagerActionTypes.SET_HUBSPOT_FIELDS
      payload: MarketplaceHubspotFieldsData[]
    }
  | {
      type: MarketplaceMappingManagerActionTypes.SET_SALESFORCE_FIELDS
      payload: MarketplaceSalesforceFieldsData[]
    }
  | {
      type: MarketplaceMappingManagerActionTypes.UPDATE_TEMPLATE_SECTION_FIELD
      payload: {
        fieldKey: keyof MarketplaceTemplatesDataFieldMappings
        updates: Partial<MarketplaceFieldMapping>
      }
    }

export const reducer: Reducer<MarketplaceMappingManagerData, actionType> = (
  state = initialState,
  action
) => {
  switch (action.type) {
    case MarketplaceMappingManagerActionTypes.SET_TEMPLATE_DATA_INFO: {
      return {
        ...state,
        templateDetails: action.payload,
      }
    }
    case MarketplaceMappingManagerActionTypes.SET_TEMPLATE_DATA: {
      return {
        ...state,
        templateData: {
          templateData: action.payload,
        },
      }
    }
    case MarketplaceMappingManagerActionTypes.SET_TEMPLATE_AS_DRAFT: {
      return {
        ...state,
        templateData: {
          ...state.templateData,
          templateData: {
            ...state.templateData.templateData,
            isDraft: action.payload,
            isActive: !action.payload,
          },
        },
      }
    }
    case MarketplaceMappingManagerActionTypes.SET_HUBSPOT_FIELDS: {
      return {
        ...state,
        hubspotFields: {
          fields: action.payload,
        },
      }
    }
    case MarketplaceMappingManagerActionTypes.SET_SALESFORCE_FIELDS: {
      return {
        ...state,
        salesforceFields: {
          fields: action.payload,
        },
      }
    }
    case MarketplaceMappingManagerActionTypes.UPDATE_TEMPLATE_SECTION_FIELD: {
      const { fieldKey, updates } = action.payload
      return {
        ...state,
        templateData: {
          ...state.templateData,
          templateData: {
            ...state.templateData.templateData,
            fieldMappings: {
              ...state.templateData.templateData.fieldMappings,
              [fieldKey]: {
                ...(state.templateData.templateData.fieldMappings?.[fieldKey] ??
                  {}),
                ...updates,
              },
            },
          },
        },
      }
    }
    default: {
      return state
    }
  }
}
